import React, { createContext, useState, useEffect, useCallback, useMemo } from "react";
import { jwtDecode } from "jwt-decode";
import { saveToken, removeToken, setAuthToken } from "../api/api";

export const AuthContext = createContext({
  isLoggedIn: false,
  token: null,
  user: null,
  login: async () => {},
  logout: () => {},
  userModalOpen: false,
  setUserModalOpen: () => {},
});

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);

  useEffect(() => {
    try {
      const savedToken = localStorage.getItem("token");
      if (savedToken) {
        setToken(savedToken);
        setAuthToken(savedToken);
        decodeUser(savedToken);
      }
    } catch (err) {
      console.warn("LocalStorage access failed:", err);
    }
  }, []);

  const decodeUser = useCallback((token) => {
    try {
      const decoded = jwtDecode(token);
      setUser({
        id: decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"],
        email: decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"],
        displayName: decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"],
        role: decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"],
      });
    } catch (err) {
      console.error("Failed to decode JWT:", err);
      setUser(null);
    }
  }, []);

  const login = useCallback((token) => {
    saveToken(token);
    setToken(token);
    setAuthToken(token);
    decodeUser(token);
  }, [decodeUser]);

  const logout = useCallback(() => {
    removeToken();
    setToken(null);
    setAuthToken(null);
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    isLoggedIn: !!token,
    token,
    user,
    login,
    logout,
    userModalOpen,
    setUserModalOpen,
  }), [token, user, login, logout, userModalOpen]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
